import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';

import {
  Dropdown,
  DropdownItem,
} from 'components/shared/DesignSystem/Dropdown';
import { ButtonIconCondensed } from '@companycam/slab-web';

import toast from 'helpers/toast';
import { Modal } from 'components/core/modal/constants';
import TASK_LIST_SECTION_TEMPLATE_UPDATE from '../gql/taskListSectionTemplateUpdate.graphql.js';
import GET_TASK_LIST_TEMPLATE_HEADER_INFO from '../gql/getTaskListTemplateHeaderInfo.graphql.js';
import { setTaskListReorderingType } from '../../../../actions/todos/taskLists.js';
import { setTemplateReorderingType } from '../../../../actions/todos/templates.js';
import { reorderTypes } from '../constants.js';
import { useModal } from 'components/shared/hooks';
import {
  useTaskListSectionSave,
  useTaskListSectionDelete,
} from 'components/projects/taskList/hooks';
import { shouldUpdateValue } from '../helpers.js';
import useSectionImageTag from 'components/projects/taskList/hooks/useSectionImageTag';
import useMenuActionTag from 'components/assetFeed/actions/useMenuActionTag';
import { SectionTitleInput } from 'components/projects/taskList/styledComponents';
import useDeleteSectionTemplate from '../hooks/templates/useDeleteSectionTemplate.js';
import { fireStevenEntityCrudEventForTemplate } from '../templates/templateUpdateStevenEvent';
import { translate } from 'translations/i18n';

const SectionContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: ${({ zIndex }) => zIndex};
  background-color: var(--cc_color_background_2);
  display: flex;
  width: 100%;
  padding: 0 var(--cc_size_spacing_s);
  margin: 0 0 var(--cc_size_spacing_m);
  border-radius: var(--cc_size_border_radius_l);
  align-items: center;

  [type='text']:disabled {
    background-color: inherit;
    cursor: default;

    &:hover {
      border: none;
    }
  }
`;

const SectionMenuContainer = styled.div`
  position: relative;
  width: var(--cc_size_spacing_xl);
  height: var(--cc_size_spacing_xl);
  display: flex;
  align-items: center;

  .dropdown--card {
    position: absolute;
    top: 0;
  }

  .dropdown--card.react .dropdown-trigger {
    width: var(--cc_size_spacing_xl);
    height: var(--cc_size_spacing_xl);
    align-items: center;
    justify-items: center;
    display: flex;
    right: 0;
    svg {
      margin: 0;

      path {
        fill: var(--cc_color_text_subtle) !important;
      }
    }
  }

  .dropdown--card .dropdown-menu {
    top: 32px;
    right: 6px;
    width: 200px;
  }
`;
// #endregion

const TaskListSectionHeader = ({
  section = {},
  listId,
  sectionInputDisabled,
  focusSectionInput,
  clearSectionInputFocus,
  isTemplate,
  showMenu,
  showReorderOption,
  showDeleteOption,
  sectionIsExpanded,
  onSectionExpandClick,
  renderSkipSection = () => {},
  zIndex,
}) => {
  const { showModal, hideModal } = useModal();
  const dispatch = useDispatch();

  const { tagSectionImagesMutation } = useSectionImageTag();
  const hasSectionPhotos = section.tasks?.nodes.some(
    (task) => task?.images?.nodes?.length,
  );
  const sectionTitleInputRef = useRef(null);
  const { deleteTasklistTemplateSection } = useDeleteSectionTemplate({
    templateId: listId,
  });

  const [openTagMenu, renderModal] = useMenuActionTag({
    assets: [],
    taggingSectionPhotos: true,
    handleTagSave: async (tagValues) => {
      await tagSectionImagesMutation({
        variables: {
          input: {
            id: section.id,
            tags: tagValues,
          },
        },
      });
    },
  });

  const onRequestClose = useCallback(
    () => hideModal(Modal.CONFIRMATION),
    [hideModal],
  );
  const [updateTasklistTemplateSection] = useMutation(
    TASK_LIST_SECTION_TEMPLATE_UPDATE,
    {
      onCompleted: () =>
        fireStevenEntityCrudEventForTemplate({
          templateNodeId: listId,
          entityType: 'checklist_template_section',
          action: 'updated',
        }),
      onError: () => {
        toast(
          translate('projects.taskList.taskListSection.updateSectionError'),
        );
      },
      refetchQueries: [
        {
          query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
          variables: { id: listId },
        },
        'GetTaskListTemplateHeaderInfo',
      ],
    },
  );
  const [title, setTitle] = useState(section.title || '');

  const { taskListSectionUpdate } = useTaskListSectionSave({
    taskListId: listId,
  });
  const { deleteTaskListSection } = useTaskListSectionDelete(listId);

  const doDelete = useCallback(() => {
    deleteTaskListSection(section);
    onRequestClose();
  }, [deleteTaskListSection, onRequestClose, section]);

  useEffect(() => {
    if (focusSectionInput) {
      sectionTitleInputRef.current.focus();
    }
  }, [focusSectionInput, sectionTitleInputRef]);

  const handleBlur = () => {
    if (focusSectionInput) clearSectionInputFocus();

    if (!shouldUpdateValue(section.title, title)) return;

    if (isTemplate) {
      updateTasklistTemplateSection({
        variables: {
          input: {
            id: section.id,
            title,
          },
        },
      });

      return;
    }

    taskListSectionUpdate({
      variables: {
        input: {
          id: section.id,
          title,
        },
      },
    });
  };

  const handleInputKeypress = (e) => {
    if (e.keyCode === 13 || e.keyCode === 27) {
      sectionTitleInputRef.current.blur();
    }
  };

  const handleOnClickDelete = () => {
    if (isTemplate) {
      showModal(Modal.CONFIRMATION, {
        confirmAction: () =>
          deleteTasklistTemplateSection({
            variables: {
              input: {
                id: section.id,
              },
            },
          }),
        confirmLabel: translate('common.confirmDelete'),
        cancelLabel: translate('common.cancelDelete'),
        message: (
          <span>
            {translate(
              'projects.taskList.taskListSectionDeleteModal.sectionDeleteBody1',
            )}{' '}
            <strong>
              {translate(
                'projects.taskList.taskListSectionDeleteModal.sectionDeleteBody2',
              )}
            </strong>
          </span>
        ),
        title: translate(
          'projects.taskList.taskListSectionDeleteModal.sectionDeleteTitle',
        ),
        onRequestClose,
      });
    } else {
      showModal(Modal.CONFIRMATION, {
        confirmAction: () => doDelete(),
        confirmLabel: translate('common.confirmDelete'),
        cancelLabel: translate('common.cancelDelete'),
        message: (
          <span>
            {translate(
              'projects.taskList.taskListSectionDeleteModal.sectionDeleteBody1',
            )}{' '}
            <strong>
              {translate(
                'projects.taskList.taskListSectionDeleteModal.sectionDeleteBody2',
              )}
            </strong>
          </span>
        ),
        title: translate(
          'projects.taskList.taskListSectionDeleteModal.sectionDeleteTitle',
        ),
        onRequestClose,
      });
    }
  };

  const handleReorderSections = () => {
    if (isTemplate) {
      dispatch(
        setTemplateReorderingType(reorderTypes.TASK_LIST_SECTION_TEMPLATES),
      );
    } else {
      dispatch(setTaskListReorderingType(reorderTypes.SECTIONS));
    }
  };

  const customTrigger = ({ triggerProps }) => (
    <ButtonIconCondensed
      accessibilityLabel={triggerProps['aria-label']}
      iconName="dots-horizontal"
      {...triggerProps}
    />
  );

  return (
    <>
      <SectionContainer zIndex={zIndex}>
        <ButtonIconCondensed
          accessibilityLabel={
            sectionIsExpanded ? 'collapse-section' : 'expand-section'
          }
          iconName={sectionIsExpanded ? 'chevron-down' : 'chevron-right'}
          onClick={onSectionExpandClick}
        />
        <SectionTitleInput
          disabled={sectionInputDisabled}
          type="text"
          value={title}
          placeholder={translate(
            'projects.taskList.taskListSection.newSection',
          )}
          onBlur={handleBlur}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleInputKeypress}
          ref={sectionTitleInputRef}
        />

        {renderSkipSection()}

        {showMenu || showDeleteOption ? (
          <SectionMenuContainer>
            <Dropdown
              style={{ top: '30px', right: '0px', width: 200 }}
              customTrigger={customTrigger}
            >
              {showReorderOption ? (
                <DropdownItem
                  className="dropdown-menu__link"
                  onClick={handleReorderSections}
                >
                  <i className="mdi mdi-sort" />
                  {translate(
                    'projects.taskList.taskListSection.reorderSections',
                  )}
                </DropdownItem>
              ) : null}

              {!isTemplate && hasSectionPhotos ? (
                <DropdownItem
                  className="dropdown-menu__link"
                  onClick={openTagMenu}
                >
                  <i className="mdi mdi-tag-plus-outline" />
                  {translate(
                    'projects.taskList.taskListSection.applyTagsToImagesWithinSection',
                  )}
                </DropdownItem>
              ) : null}

              {showDeleteOption ? (
                <DropdownItem
                  className="dropdown-menu__link"
                  onClick={handleOnClickDelete}
                >
                  <i className="mdi mdi-delete-outline" />
                  {translate('projects.taskList.taskListSection.deleteSection')}
                </DropdownItem>
              ) : null}
            </Dropdown>
          </SectionMenuContainer>
        ) : null}
      </SectionContainer>
      {renderModal()}
    </>
  );
};

TaskListSectionHeader.propTypes = {
  section: PropTypes.object,
  listId: PropTypes.string.isRequired,
  sectionInputDisabled: PropTypes.bool,
  focusSectionInput: PropTypes.bool,
  clearSectionInputFocus: PropTypes.func.isRequired,
  isTemplate: PropTypes.bool,
  showMenu: PropTypes.bool,
  showReorderOption: PropTypes.bool.isRequired,
  showDeleteOption: PropTypes.bool,
  renderSkipSection: PropTypes.func,
  sectionIsExpanded: PropTypes.bool,
  onSectionExpandClick: PropTypes.func,
  zIndex: PropTypes.number,
};

export default TaskListSectionHeader;
